import { ChevronDown, ChevronLeft, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "../lib/cn.js";
import type { NavSection } from "../lib/nav.js";
import type { Session } from "../lib/session.js";
import { Logo } from "./Logo.js";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "./ui/sidebar.js";

/**
 * The navigation, built to match legacy's (`components/app-sidebar.jsx` + `nav-main.jsx`):
 * wordmark and a collapse control at the top, collapsible sections in the middle, the
 * signed-in person at the bottom.
 *
 * A SECTION CONTAINING THE ACTIVE PAGE STARTS OPEN, and the section header itself turns
 * solid. Legacy does both, and together they answer "where am I" without the merchant
 * having to open anything — which is the entire job of a sidebar on a dashboard with
 * four groups of reports.
 *
 * SINGLE-OPEN ACCORDION (fixed 2026-09-17, owner report). `openSection` is ONE value
 * here — not per-`Section` local state — so opening a section always closes whichever one
 * was open before, matching legacy's `openGroup` (`nav-main.jsx`). Before this it was a
 * `useState` INSIDE `Section`, one independent copy per rendered section with nothing
 * coordinating them, so every section a merchant had ever opened stayed open forever and
 * the whole list stacked to the bottom of the sidebar.
 */
export function AppSidebar({
  session,
  sections,
  route,
  onNavigate,
  onSignOut,
}: {
  readonly session: Session;
  /** Already filtered for this viewer's role and setup state (see visibleNavSections). */
  readonly sections: readonly NavSection[];
  readonly route: string | null;
  readonly onNavigate: (to: string) => void;
  readonly onSignOut: () => Promise<void>;
}): React.JSX.Element {
  const { open, toggle } = useSidebar();

  const [openSection, setOpenSection] = useState<string | null>(() => {
    const active = sections.find(
      (section) => section.items !== undefined && section.items.some((item) => item.to === route),
    );
    return active?.label ?? null;
  });

  return (
    <Sidebar>
      <SidebarHeader className="justify-between">
        {open && <Wordmark />}
        <button
          type="button"
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          onClick={toggle}
          className="text-muted-foreground hover:bg-accent hover:text-foreground ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md border transition-colors"
        >
          <ChevronLeft className={cn("size-4 transition-transform", !open && "rotate-180")} />
        </button>
      </SidebarHeader>

      <SidebarContent className="gap-1 px-2">
        {sections.map((section) => (
          <Section
            key={section.label}
            section={section}
            route={route}
            // Plain navigate — used for a SUB-item's own click. The merchant is still
            // inside this group, just on a different page in it, so nothing about the
            // accordion should change (bug fixed 2026-09-17: this used to be the SAME
            // closing wrapper as `onNavigateAndClose` below for every click, sub-items
            // included, so picking "Meta Campaign Attribution" also collapsed "Perf" —
            // exactly backwards from what opening a group is for).
            onNavigate={onNavigate}
            // Closing navigate — used ONLY for a section that IS a page (Billing, Team,
            // Connect a store, …). Leaving a group via an unrelated standalone link
            // shouldn't leave it silently expanded behind you — matches legacy's
            // `handleMainItemClick` in `nav-main.jsx`.
            onNavigateAndClose={(to) => {
              setOpenSection(null);
              onNavigate(to);
            }}
            collapsed={!open}
            isOpen={openSection === section.label}
            onToggleOpen={() =>
              setOpenSection((current) => (current === section.label ? null : section.label))
            }
            onExpandSidebar={() => {
              if (!open) toggle();
            }}
          />
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t pt-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
            {(session.user.name.trim() === "" ? session.user.email : session.user.name).slice(0, 1).toUpperCase()}
          </div>
          {open && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{session.user.name}</p>
              <p className="text-muted-foreground truncate text-xs">{session.user.email}</p>
            </div>
          )}
          <button
            type="button"
            aria-label="Sign out"
            onClick={() => void onSignOut()}
            className="text-muted-foreground hover:bg-accent hover:text-foreground ml-auto inline-flex size-8 shrink-0 items-center justify-center rounded-md transition-colors"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

/**
 * The wordmark, set in type rather than shipped as legacy's SVG.
 *
 * `ecom_code/client/public/ecomlogo.svg` is legacy's asset; copying a brand file across
 * repositories is the owner's call to make, not a detail to slip into a refactor. The two
 * brand colours are legacy's own, so this reads as the same mark until that call is made.
 */
function Wordmark(): React.JSX.Element {
  return (
    <Logo className="h-6 w-auto" />
  );
}

/** Hover-flyout close delay — legacy's own value (`nav-main.jsx`'s `scheduleCloseFlyout`). */
const FLYOUT_CLOSE_DELAY_MS = 150;

function Section({
  section,
  route,
  onNavigate,
  onNavigateAndClose,
  collapsed,
  isOpen,
  onToggleOpen,
  onExpandSidebar,
}: {
  readonly section: NavSection;
  readonly route: string | null;
  readonly onNavigate: (to: string) => void;
  readonly onNavigateAndClose: (to: string) => void;
  readonly collapsed: boolean;
  /** Whether THIS section is the one open accordion entry — ignored for a `to`-only section. */
  readonly isOpen: boolean;
  readonly onToggleOpen: () => void;
  readonly onExpandSidebar: () => void;
}): React.JSX.Element {
  const containsActive = section.to === route || (section.items ?? []).some((item) => item.to === route);
  const Icon = section.icon;

  // A section that IS a page: a plain link, no disclosure.
  if (section.to !== undefined) {
    return (
      <a
        href={`/${section.to}`}
        aria-current={containsActive ? "page" : undefined}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          onNavigateAndClose(section.to ?? "");
        }}
        className={cn(
          "flex h-10 shrink-0 items-center gap-2.5 rounded-lg px-3 text-sm font-medium transition-colors",
          containsActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
          collapsed && "justify-center px-0",
        )}
      >
        <Icon className="size-4 shrink-0" />
        {!collapsed && <span className="truncate">{section.label}</span>}
      </a>
    );
  }

  // Collapsed (icon-only) sidebar: a hover flyout showing the section name + its pages —
  // matches legacy's `nav-main.jsx` collapsed branch. The expanded accordion below it
  // never renders sub-items while collapsed, so without this a merchant who collapses the
  // sidebar cannot reach a report inside a group at all.
  if (collapsed) {
    return (
      <div className="shrink-0">
        <GroupFlyout
        section={section}
        route={route}
        containsActive={containsActive}
        onNavigate={onNavigate}
        onOpen={() => {
          onToggleOpen();
          onExpandSidebar();
        }}
      />
      </div>
    );
  }

  return (
    <div className="shrink-0">
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={isOpen}
        className={cn(
          "flex h-10 w-full shrink-0 items-center gap-2.5 rounded-lg px-3 text-sm font-medium transition-colors",
          containsActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
        )}
      >
        <Icon className="size-4 shrink-0" />
        <span className="truncate">{section.label}</span>
        <ChevronDown className={cn("ml-auto size-4 shrink-0 transition-transform", !isOpen && "-rotate-90")} />
      </button>

      {isOpen && (
        <ul className="mt-1 flex flex-col gap-0.5 pl-3">
          {(section.items ?? []).map((item) => {
            const isActive = item.to === route;
            return (
              <li key={item.to} className="shrink-0">
                <a
                  href={`/${item.to}`}
                  aria-current={isActive ? "page" : undefined}
                  onClick={(event) => {
                    // Let the browser handle gestures that MEAN "somewhere else" — a new
                    // tab, a new window. Only a plain left click is ours.
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                    event.preventDefault();
                    onNavigate(item.to);
                  }}
                  className={cn(
                    "flex min-h-9 shrink-0 items-center rounded-md px-3 py-1.5 text-sm transition-colors",
                    isActive ? "text-primary font-medium bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-accent",
                  )}
                >
                  <span className="truncate">{item.label}</span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The collapsed-sidebar hover flyout. Rendered through a PORTAL rather than positioned
 * `absolute` in place: `SidebarContent` is `overflow-auto` (so the nav list itself can
 * scroll), and an absolutely-positioned child cannot escape its ancestor's clipping — the
 * flyout would be cut off at the sidebar's own edge instead of floating beside it, exactly
 * where legacy's `PopoverContent` (which portals by default) shows it.
 */
function GroupFlyout({
  section,
  route,
  containsActive,
  onNavigate,
  onOpen,
}: {
  readonly section: NavSection;
  readonly route: string | null;
  readonly containsActive: boolean;
  readonly onNavigate: (to: string) => void;
  readonly onOpen: () => void;
}): React.JSX.Element {
  const [hovered, setHovered] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const Icon = section.icon;

  const openFlyout = (): void => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect !== undefined) setCoords({ top: rect.top, left: rect.right + 10 });
    setHovered(true);
  };

  const scheduleClose = (): void => {
    closeTimer.current = setTimeout(() => setHovered(false), FLYOUT_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    };
  }, []);

  return (
    <div onMouseEnter={openFlyout} onMouseLeave={scheduleClose}>
      <button
        ref={triggerRef}
        type="button"
        onClick={onOpen}
        aria-expanded={hovered}
        className={cn(
          "flex h-10 w-full items-center justify-center rounded-lg transition-colors",
          containsActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
        )}
      >
        <Icon className="size-4 shrink-0" />
      </button>

      {hovered && coords !== null &&
        createPortal(
          <div
            role="menu"
            onMouseEnter={openFlyout}
            onMouseLeave={scheduleClose}
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            className="bg-popover text-popover-foreground z-50 w-56 rounded-xl border py-1 shadow-lg"
          >
            <div className="border-b px-3.5 py-2.5 text-sm font-semibold">{section.label}</div>
            <div className="flex flex-col gap-0.5 p-1.5">
              {(section.items ?? []).map((item) => {
                const isActive = item.to === route;
                return (
                  <a
                    key={item.to}
                    href={`/${item.to}`}
                    role="menuitem"
                    aria-current={isActive ? "page" : undefined}
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                      event.preventDefault();
                      setHovered(false);
                      onNavigate(item.to);
                    }}
                    className={cn(
                      "rounded-md px-2.5 py-2 text-sm transition-colors",
                      isActive ? "text-primary bg-primary/10 font-medium" : "text-foreground hover:bg-accent",
                    )}
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
